const LOGO_BACKGROUND = '#ffffff';
const FETCH_TIMEOUT_MS = 5_000;
const RASTER_SCALE = 2;

export const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = src;
  });

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const fetchUrlAsDataUrl = async (url: string): Promise<string | null> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) {
      return null;
    }
    return blobToDataUrl(await response.blob());
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

const resolveToDataUrl = async (src: string): Promise<string | null> => {
  if (src.startsWith('data:')) {
    return src;
  }
  return fetchUrlAsDataUrl(src);
};

interface DrawImageToPngArgs {
  image: CanvasImageSource;
  width: number;
  height: number;
  clipCircle?: boolean;
  backgroundColor?: string;
}

export const drawImageToPng = ({
  image,
  width,
  height,
  clipCircle = false,
  backgroundColor = LOGO_BACKGROUND,
}: DrawImageToPngArgs): string => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * RASTER_SCALE));
  canvas.height = Math.max(1, Math.round(height * RASTER_SCALE));
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to acquire 2d canvas context');
  }

  ctx.scale(RASTER_SCALE, RASTER_SCALE);
  if (clipCircle) {
    ctx.beginPath();
    ctx.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, Math.PI * 2);
    ctx.fillStyle = backgroundColor;
    ctx.fill();
    ctx.clip();
  }
  ctx.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL('image/png');
};

interface RasterizeSrcToPngArgs {
  src: string;
  width: number;
  height?: number;
  clipCircle?: boolean;
  backgroundColor?: string;
}

export const rasterizeSrcToPng = async ({
  src,
  width,
  height,
  clipCircle = false,
  backgroundColor,
}: RasterizeSrcToPngArgs): Promise<string | null> => {
  const dataUrl = await resolveToDataUrl(src);
  if (!dataUrl) {
    return null;
  }

  try {
    const image = await loadImage(dataUrl);
    const resolvedHeight =
      height ??
      (image.naturalWidth
        ? width * (image.naturalHeight / image.naturalWidth)
        : width);
    return drawImageToPng({
      image,
      width,
      height: resolvedHeight,
      clipCircle,
      backgroundColor,
    });
  } catch {
    return null;
  }
};

interface CreateSizedImageArgs {
  src: string;
  width: number;
  height: number;
  alt?: string;
}

export const createSizedImage = ({
  src,
  width,
  height,
  alt = '',
}: CreateSizedImageArgs): HTMLImageElement => {
  const img = document.createElement('img');
  img.src = src;
  img.alt = alt;
  img.width = width;
  img.height = height;
  img.style.width = `${width}px`;
  img.style.height = `${height}px`;
  img.style.display = 'inline-block';
  img.style.verticalAlign = 'middle';
  return img;
};

export const swapImagesToLightTheme = (root: HTMLElement) => {
  root.querySelectorAll('img').forEach(img => {
    if (img.src) {
      img.src = img.src.replace(/\/light\.png$/, '/dark.png');
    }
  });
};

const rasterizeLogoImage = async (img: HTMLImageElement) => {
  const wrapper = img.parentElement;
  const target = wrapper ?? img;
  const rect = target.getBoundingClientRect();
  const size = Math.max(
    Math.round(rect.width) || Math.round(rect.height) || img.width || 48,
    1
  );
  const backgroundColor =
    wrapper instanceof HTMLElement
      ? window.getComputedStyle(wrapper).backgroundColor
      : LOGO_BACKGROUND;
  const png = await rasterizeSrcToPng({
    src: img.src,
    width: size,
    height: size,
    clipCircle: true,
    backgroundColor,
  });
  if (!png) {
    return;
  }
  target.replaceWith(
    createSizedImage({ src: png, width: size, height: size, alt: img.alt })
  );
};

const CSS_BACKGROUND_IMAGE_URL_PATTERN = /url\(["']?(.+?)["']?\)/;

const getCssBackgroundImageUrl = (element: HTMLElement): string | null => {
  const backgroundImage = window.getComputedStyle(element).backgroundImage;
  if (!backgroundImage || backgroundImage === 'none') {
    return null;
  }
  return CSS_BACKGROUND_IMAGE_URL_PATTERN.exec(backgroundImage)?.[1] ?? null;
};

// FlagIcon paints via CSS background-image (flag-icons CDN). SVG foreignObject
// snapshots, paste destinations, and print iframes will not fetch those URLs.
export const embedFlagIcons = async (root: HTMLElement) => {
  const flags = Array.from(
    root.querySelectorAll<HTMLElement>('span.fi[data-country]')
  );

  for (const flag of flags) {
    const src = getCssBackgroundImageUrl(flag);
    if (!src) {
      continue;
    }

    const computed = window.getComputedStyle(flag);
    const rect = flag.getBoundingClientRect();
    const width = Math.max(Math.round(rect.width) || flag.offsetWidth || 1, 1);
    const height = Math.max(
      Math.round(rect.height) || flag.offsetHeight || 1,
      1
    );
    const clipCircle =
      flag.classList.contains('fis') || computed.borderRadius === '50%';
    const png = await rasterizeSrcToPng({
      src,
      width,
      height,
      clipCircle,
      backgroundColor: 'transparent',
    });
    if (!png) {
      continue;
    }

    const image = createSizedImage({
      src: png,
      width,
      height,
      alt: flag.dataset.country ?? '',
    });
    image.style.flexShrink = '0';
    image.style.borderRadius = computed.borderRadius;
    image.style.overflow = 'hidden';
    flag.replaceWith(image);
  }
};

export const embedExternalImages = async (root: HTMLElement) => {
  await embedFlagIcons(root);

  const images = Array.from(root.querySelectorAll('img')).filter(
    img => img.src && !img.src.startsWith('data:')
  );

  for (const img of images) {
    if (img.alt === 'logo-image') {
      await rasterizeLogoImage(img);
      continue;
    }

    const rect = img.getBoundingClientRect();
    const width = Math.max(
      Math.round(rect.width) || img.naturalWidth || img.width || 1,
      1
    );
    const height = Math.max(
      Math.round(rect.height) || img.naturalHeight || img.height || 1,
      1
    );
    const isSvg = /\.svg(\?|$)/i.test(img.src);
    const png = await rasterizeSrcToPng({ src: img.src, width, height });
    if (png) {
      img.src = png;
      img.style.width = `${width}px`;
      img.style.height = `${height}px`;
      continue;
    }
    if (isSvg) {
      img.remove();
    }
  }
};
